import { colors } from '@kmns-internal/furikake'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import FileUploadOutlined from '@mui/icons-material/FileUploadOutlined'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import * as Sentry from '@sentry/browser'
import {
  type InformationFile,
  ResponseTypeEnum,
  type ResponseURL,
  type TemplateNodeSchema,
  type TemplateQuestion,
} from '@ulysses-inc/harami_api_client'
import { type FC, type Ref, useCallback, useRef, useState } from 'react'
import { useDrop } from 'react-dnd'
import { NativeTypes } from 'react-dnd-html5-backend'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { getMBSize } from 'src/exShared/util/fileSize'
import { UUID } from 'src/exShared/util/uuid'
import { getPdfThumbUrlFromUrl } from 'src/state/ducks/files/saga'
import {
  updateTemplateNode,
  uploadInformationFiles,
} from 'src/state/ducks/templates/actions'
import type { RootState } from 'src/state/store'
import { Container, InformationError } from './dumb'

type FileInfo = {
  uid: string
  name: string
  url: string
  thumbUrl?: string | null
}

type InformationType = 'file' | 'url'

const getInformationType = (question: TemplateQuestion): InformationType => {
  switch (question.responseType) {
    case ResponseTypeEnum.INFORMATION: {
      return 'file'
    }
    case ResponseTypeEnum.URL: {
      return 'url'
    }
    default: {
      return 'file'
    }
  }
}

type Props = {
  question: TemplateQuestion
  isLoadingInformation: boolean
  errorInformation: string
  fileList: FileInfo[]
  onChangeInformationType: (informationType: InformationType) => void
  onSelectFile: (file: File) => void
  onRemoveFile: () => void
  onChangeURL: (url: string) => void
}

const InformationOption: FC<Props> = ({
  question,
  isLoadingInformation,
  errorInformation,
  fileList,
  onChangeInformationType,
  onSelectFile,
  onRemoveFile,
  onChangeURL,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)

  // アプリ全体が react-dnd の HTML5Backend 配下にあり、ネイティブのファイルドラッグも
  // backend が window レベルで横取りするため、素の onDrop ハンドラは発火しない。
  // react-dnd の drop target (NativeTypes.FILE) として登録し backend 経由で受け取る。
  const [, dropFileRef] = useDrop(
    () => ({
      accept: [NativeTypes.FILE],
      drop: (item: { files: File[] }) => {
        const file = item.files?.[0]
        if (file) onSelectFile(file)
      },
    }),
    [onSelectFile],
  )

  if (question === undefined) return null

  const informationType = getInformationType(question)
  const responseURL = question.responseURLs?.[0]

  return (
    <Container sx={{ pb: 1, px: 1, columnGap: 2 }}>
      <Select<InformationType>
        value={informationType}
        size="small"
        sx={{ width: 120, flexShrink: 0 }}
        onChange={(e: SelectChangeEvent<InformationType>) =>
          onChangeInformationType(e.target.value as InformationType)
        }
      >
        <MenuItem value="file">ファイル</MenuItem>
        <MenuItem value="url">URL</MenuItem>
      </Select>
      {question.responseType === ResponseTypeEnum.INFORMATION && (
        <>
          <Box
            ref={dropFileRef as Ref<HTMLDivElement>}
            sx={{ position: 'relative' }}
          >
            {isLoadingInformation && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1,
                  bgcolor: 'rgba(255, 255, 255, 0.7)',
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.pdf"
              style={{ display: 'none' }}
              onChange={e => {
                const file = e.target.files?.[0]
                if (file) onSelectFile(file)
                e.target.value = ''
              }}
            />
            <Stack direction="row" alignItems="center" gap={1}>
              <Button
                variant="outlined"
                startIcon={<FileUploadOutlined />}
                onClick={() => fileInputRef.current?.click()}
              >
                ファイルを選択
              </Button>
              {fileList.map(file => {
                const displayUrl = file.thumbUrl ?? file.url
                return (
                  <Stack
                    key={file.uid}
                    direction="row"
                    alignItems="center"
                    sx={{ mt: 0.5 }}
                  >
                    {displayUrl ? (
                      <Box
                        component="a"
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'inline-flex',
                          mr: 1,
                          lineHeight: 0,
                        }}
                      >
                        <Box
                          component="img"
                          src={displayUrl}
                          alt={file.name}
                          sx={{
                            width: 48,
                            height: 48,
                            objectFit: 'cover',
                            border: '1px solid',
                            borderColor: 'divider',
                            cursor: 'pointer',
                          }}
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          mr: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: colors.gray[0],
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <InsertDriveFileOutlined />
                      </Box>
                    )}
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {file.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={onRemoveFile}
                      aria-label="ファイルを削除"
                    >
                      <DeleteOutlineIcon sx={{ color: colors.error.main }} />
                    </IconButton>
                  </Stack>
                )
              })}
            </Stack>
          </Box>
          <InformationError>{errorInformation}</InformationError>
        </>
      )}
      {question.responseType === ResponseTypeEnum.URL && (
        <>
          <TextField
            value={responseURL?.url ?? ''}
            placeholder="httpsもしくはhttpから始まるURLを入力してください（例: https://example.com）"
            size="small"
            sx={{ flexGrow: 1 }}
            onChange={e => onChangeURL(e.target.value)}
          />
          <Tooltip
            placement="bottom-start"
            title="設定したWebページにログインすると、ログイン状態が保持され、他のユーザーがログイン後のページを閲覧できる可能性があります。共有してはいけない情報を含む場合は注意してください。"
          >
            <InfoOutlined />
          </Tooltip>
        </>
      )}
    </Container>
  )
}

type ContainerProps = {
  node: TemplateNodeSchema
}

export const InformationOptionContainer: FC<ContainerProps> = ({ node }) => {
  const dispatch = useDispatch()

  const isLoadingInformation = useSelector(
    (state: RootState) =>
      state.templatesState.templatePages.isLoadingInformation,
    shallowEqual,
  )
  const [errorInformation, setErrorInformation] = useState<string>('')

  const handleChangeInformationType = useCallback(
    (informationType: InformationType) => {
      switch (informationType) {
        case 'file': {
          dispatch(
            updateTemplateNode(node.id, {
              ...node,
              question: {
                ...node.question,
                responseType: ResponseTypeEnum.INFORMATION,
                responseInformations: [],
                responseURLs: [],
              },
            }),
          )
          break
        }
        case 'url': {
          dispatch(
            updateTemplateNode(node.id, {
              ...node,
              question: {
                ...node.question,
                responseType: ResponseTypeEnum.URL,
                responseInformations: [],
                responseURLs: [{ url: '' }],
              },
            }),
          )
          break
        }
      }
    },
    [dispatch, node],
  )

  const handleSelectFile = useCallback(
    (file: File) => {
      setErrorInformation('')
      if (file.type !== 'application/pdf' && file.size > getMBSize(2)) {
        setErrorInformation('画像ファイルの最大容量は2MBです')
        return
      }

      // カジュアルにinputタグのaccept属性で指定してるファイル形式以外をアップロード出来ないようにバリデーションしておく
      const acceptedTypes = [
        'image/png',
        'image/jpeg',
        'application/pdf',
        // 👇️accept属性で指定していないが、このバリデーションを入れる前の既存の挙動でアップロード可能だったので、念の為許可している
        'image/gif',
      ]
      if (!acceptedTypes.includes(file.type)) {
        setErrorInformation('許可されていないファイル形式です')
        return
      }

      dispatch(uploadInformationFiles([file], node.id))
    },
    [dispatch, node],
  )

  const handleRemoveFile = useCallback(() => {
    dispatch(
      updateTemplateNode(node.id, {
        ...node,
        question: {
          ...node.question,
          responseInformations: [],
        },
      }),
    )
  }, [dispatch, node])

  const handleChangeURL = useCallback(
    (url: string) => {
      const responseURL = node.question?.responseURLs?.[0]
      // 回答種別がURLにもかからわずresponseURLがundefinedになる事象が発生したため、
      // undefinedの場合にもurlが変更できるようにした。
      // https://kaminashi.atlassian.net/browse/HPB-5228
      if (responseURL === undefined) {
        Sentry.captureException(new Error('responseURLが存在しません。'))
      }
      const newResponseURL: ResponseURL = {
        ...responseURL,
        url,
      }
      dispatch(
        updateTemplateNode(node.id, {
          ...node,
          question: {
            ...node.question,
            responseInformations: [],
            responseURLs: [newResponseURL],
          },
        }),
      )
    },
    [dispatch, node],
  )

  const fileList: FileInfo[] = (node.question?.responseInformations ?? []).map(
    (file: InformationFile) => {
      const maxFilename = 50
      return {
        uid: file.uuid ?? UUID(),
        name: file.name?.slice(0, maxFilename) ?? '',
        url: file.url ?? '',
        ...(file.url?.match('.pdf') && {
          thumbUrl: getPdfThumbUrlFromUrl(file.url),
        }),
      }
    },
  )

  if (node.question === undefined) {
    return null
  }

  return (
    <InformationOption
      question={node.question}
      isLoadingInformation={isLoadingInformation}
      errorInformation={errorInformation}
      fileList={fileList}
      onChangeInformationType={handleChangeInformationType}
      onSelectFile={handleSelectFile}
      onRemoveFile={handleRemoveFile}
      onChangeURL={handleChangeURL}
    />
  )
}
